import type { ApiError } from './types';

/**
 * The authenticated API client.
 *
 * <p>One place that knows how to attach a token, how to read the error contract, and what a 401
 * means. Scattering `fetch` calls through features is how one of them ends up missing the token, or
 * swallowing an error the operator needed to see.
 */

/** An error carrying the server's stable code, so callers branch on `code` and never on wording. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }

  /** The operator's own permissions are wrong, as opposed to their input. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** The request conflicted with current state — an illegal transition, a duplicate email. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

/**
 * Supplies the bearer token for each request.
 *
 * <p>Injected rather than imported so the client does not depend on the auth library, which keeps
 * it testable without standing up an OIDC provider.
 */
type TokenProvider = () => string | undefined;

let getToken: TokenProvider = () => undefined;
let onUnauthorized: () => void = () => {};

/** Wired once at startup by the auth provider. */
export function configureApiClient(options: {
  getToken: TokenProvider;
  onUnauthorized: () => void;
}): void {
  getToken = options.getToken;
  onUnauthorized = options.onUnauthorized;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Multipart uploads set their own content type and pass a FormData body. */
  formData?: FormData;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(path, {
    method: options.method ?? 'GET',
    headers,
    body: options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
    signal: options.signal,
  });

  if (response.status === 401) {
    // The token expired or was never valid. Restarting the login flow is the only useful action,
    // and every caller would otherwise have to remember to do it.
    onUnauthorized();
    throw new ApiRequestError(401, 'UNAUTHENTICATED', 'Your session has expired');
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  return (await response.json()) as T;
}

/**
 * Turns a failed response into an error carrying the server's code.
 *
 * <p>Falls back to the status when the body is not the documented shape — a proxy error page or a
 * gateway timeout is not going to be JSON, and the panel should still say something truthful.
 */
async function toApiError(response: Response): Promise<ApiRequestError> {
  try {
    const body = (await response.json()) as ApiError;
    return new ApiRequestError(
      response.status,
      body.error ?? 'UNKNOWN',
      body.message ?? 'The request failed',
    );
  } catch {
    return new ApiRequestError(
      response.status,
      'UNKNOWN',
      `The request failed with status ${response.status}`,
    );
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),

  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),

  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),

  // Added for mediation chains (ADS-0034), the first resource this panel deleted. ADS-0047 added
  // advertisers and campaigns, but only while they have NO history: one recorded event or granted
  // reward and the server refuses, because a disputed charge has to stay explainable. Anything
  // that has traded is deactivated, exactly as before.
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', formData }),
};

/** Builds a query string, omitting empty values so the URL stays readable. */
export function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  });
  const rendered = search.toString();
  return rendered ? `?${rendered}` : '';
}
